import { useEffect, useState } from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import VersionList from './VersionList';
import { extensionApis } from '../../../apis/extensionApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { sentenceCase } from '../../../utils/utils';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';
import Modal from '../../sections/modal/Modal';

const DownloadExtensionButton = ({
  extension,
  onAddExtension,
  ...restProps
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [extensionDetails, setExtensionDetails] = useState([]);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const toggleModal = () => setOpen(!open);

  // Use effect to fetch extension details
  useEffect(() => {
    const getExtensionDetails = async () => {
      setLoading(true);
      try {
        const request = await extensionApis.getExtension(extension.id);
        setExtensionDetails(request.data.versions);
      } catch (error) {
        showToast(
          TOAST_TYPE.ERROR,
          t('extensionVersionsError', { ns: 'errors' })
        );
      }
      setLoading(false);
    };
    // Only fetch details when the modal is open and the extension details are not already fetched or maybe still empty
    if (open && isEmpty(extensionDetails)) getExtensionDetails();
  }, [open]);

  const handleAddExtension = async (data) => {
    await onAddExtension(data);
    toggleModal();
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={toggleModal}
        aria-label="download extension"
      >
        {t('download', { ns: 'buttons' })}
      </Button>
      <Modal openModal={open} title={extension.name} handleClose={toggleModal}>
        {extension.description ? (
          <Text aria-label="extension description">
            {extension.description}
          </Text>
        ) : (
          <Text color="textSecondary">
            {sentenceCase(
              t('empty', {
                ns: 'descriptions',
                text: t('ext.details', { ns: 'project' }),
              })
            )}
          </Text>
        )}
        {extension.descriptionUrl && (
          <Button
            variant="text"
            target="_blank"
            href={extension.descriptionUrl}
            rel="noreferrer"
            endIcon={<LaunchIcon />}
            style={{ marginTop: 8 }}
          >
            {t('readMoreOnLink', { ns: 'buttons' })}
          </Button>
        )}

        {loading ? (
          <Loading />
        ) : isEmpty(extensionDetails) ? (
          <Box sx={{ my: 1 }}>
            <Alert severity="error" aria-label="no-extension-versions">
              {t('notFound', {
                ns: 'descriptions',
                text: t('extensionVersions', { ns: 'titles' }),
              })}
            </Alert>
          </Box>
        ) : (
          <VersionList
            extensionType={extension.type}
            versions={extensionDetails}
            handleAdd={handleAddExtension}
            {...restProps}
          />
        )}
      </Modal>
    </>
  );
};

DownloadExtensionButton.propTypes = {
  extension: PropTypes.object.isRequired,
  onAddExtension: PropTypes.func.isRequired,
};

export default DownloadExtensionButton;
